import React, { useState, useRef, useEffect, useMemo } from 'react';
import { buildApiUrl } from '../config/constants';
import { useAuth } from '../context/AuthContext';
import AnnotationsService, { Annotation as ApiAnnotation } from '../services/AnnotationsService';
import { isTokenExpired } from '../utils/auth';

// CSS for improved EPUB formatting
const epubStyles = {
  epubContent: {
    fontFamily: 'Georgia, "Times New Roman", serif',
    lineHeight: '1.8',
    textAlign: 'justify' as const,
    hyphens: 'auto' as const,
    textRendering: 'optimizeLegibility' as const,
    wordSpacing: '0.1em',
    letterSpacing: '0.02em',
    maxWidth: '100%',
    padding: '0',
    margin: '0'
  },
  dialogueParagraph: {
    marginTop: '1.2em',
    marginBottom: '1.2em',
    textIndent: '0',
    position: 'relative' as const
  },
  dialogueLine: {
    display: 'block' as const,
    marginBottom: '0.8em',
    paddingLeft: '1.5em',
    borderLeft: '3px solid #e5e7eb'
  },
  dialogueMarker: {
    fontWeight: 'bold' as const,
    color: '#374151',
    marginRight: '0.5em'
  },
  chapterTitle: {
    fontSize: '1.4em',
    fontWeight: 'bold' as const,
    textAlign: 'center' as const,
    margin: '2em 0 1.5em 0',
    color: '#1f2937',
    borderBottom: '2px solid #e5e7eb',
    paddingBottom: '0.5em'
  },
  firstLetter: {
    fontSize: '3em',
    fontWeight: 'bold' as const,
    float: 'left' as const,
    lineHeight: '0.8',
    margin: '0.1em 0.1em 0 0',
    color: '#374151'
  }
};

interface Annotation {
  id: string | number;
  type: 'highlight' | 'note' | 'bookmark';
  content?: string;
  color?: string;
  position: {
    startOffset: number;
    endOffset: number;
    page: number;
  };
  createdAt: Date | string;
}

// SVG Icons components
const MinusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const SunIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const MoonIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
  </svg>
);

const BookmarkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const HighlightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const NoteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
  </svg>
);

const AnnotationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

// Button icons (with margin)
const ButtonHighlightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const ButtonNoteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
  </svg>
);

const ButtonBookmarkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
  </svg>
);

// Small icons for annotations list
const SmallHighlightIcon = ({color}: {color?: string}) => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke={color || 'currentColor'} style={color ? {color} : undefined}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const SmallNoteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
  </svg>
);

const SmallBookmarkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
  </svg>
);

const SmallCloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

interface BookReaderPreviewProps {
  book: {
    id: number;
    title: string;
    author: string;
    coverImage: string;
    content?: string;
    totalPages?: number;
    currentPage?: number;
    fileName?: string;
    fileType?: string;
  };
  onClose: () => void;
}

const BookReaderPreview: React.FC<BookReaderPreviewProps> = ({ book, onClose }) => {
  const { user, token, logout } = useAuth();
  const [currentPage, setCurrentPage] = useState(book.currentPage || 1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fontSize, setFontSize] = useState(16);
  const [viewMode, setViewMode] = useState<'preview' | 'read'>('preview');
  const [readingMode, setReadingMode] = useState<'day' | 'night'>('day');
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [activeAnnotationTool, setActiveAnnotationTool] = useState<'highlight' | 'note' | 'bookmark' | null>(null);
  const [highlightColor, setHighlightColor] = useState<string>('#FFEB3B');
  const [showAnnotationsPanel, setShowAnnotationsPanel] = useState<boolean>(true);
  const [selectedText, setSelectedText] = useState<string>('');
  const [isTextToSpeechActive, setIsTextToSpeechActive] = useState<boolean>(false);
  const [currentAnnotation, setCurrentAnnotation] = useState<Annotation | null>(null);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(true);
  const [bookContent, setBookContent] = useState<string>(book.content || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalPages, setTotalPages] = useState(book.totalPages || 20);
  const [noteContent, setNoteContent] = useState<string>(''); // New state for note content
  const [showNoteDialog, setShowNoteDialog] = useState(false); // New state for note dialog visibility
  const [tempAnnotation, setTempAnnotation] = useState<{ text: string, startOffset: number, endOffset: number } | null>(null); // For storing text selection temporarily
  const [bookLanguage, setBookLanguage] = useState<string>('en');
  const [contentFormat, setContentFormat] = useState<'txt' | 'epub' | 'pdf' | 'unknown'>(() => {
    if (book.fileType === 'text/plain' || book.fileName?.toLowerCase().endsWith('.txt')) {
      return 'txt';
    }
    if (book.fileType === 'application/epub+zip' || book.fileName?.toLowerCase().endsWith('.epub')) {
      return 'epub';
    }
    if (book.fileType === 'application/pdf' || book.fileName?.toLowerCase().endsWith('.pdf')) {
      return 'pdf';
    }
    return 'unknown';
  });
  
  const contentRef = useRef<HTMLDivElement>(null);
  const speechSynthesisRef = useRef<SpeechSynthesis | null>(null);
  const speechUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  const detectContentFormat = (
    fileName?: string,
    fileType?: string,
    apiFormat?: string
  ): 'txt' | 'epub' | 'pdf' | 'unknown' => {
    if (apiFormat === 'txt' || fileType === 'text/plain' || fileName?.toLowerCase().endsWith('.txt')) {
      return 'txt';
    }
    if (apiFormat === 'epub' || fileType === 'application/epub+zip' || fileName?.toLowerCase().endsWith('.epub')) {
      return 'epub';
    }
    if (apiFormat === 'pdf' || fileType === 'application/pdf' || fileName?.toLowerCase().endsWith('.pdf')) {
      return 'pdf';
    }
    return 'unknown';
  };

  const decodeTextEntities = (content: string) => {
    return content
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&hellip;/g, '...')
      .replace(/&mdash;/g, '—')
      .replace(/&ndash;/g, '–')
      .replace(/&lsquo;/g, "'")
      .replace(/&rsquo;/g, "'")
      .replace(/&ldquo;/g, '"')
      .replace(/&rdquo;/g, '"')
      .replace(/&#259;/g, 'ă')
      .replace(/&#258;/g, 'Ă')
      .replace(/&#226;/g, 'â')
      .replace(/&#194;/g, 'Â')
      .replace(/&#238;/g, 'î')
      .replace(/&#206;/g, 'Î')
      .replace(/&#537;/g, 'ș')
      .replace(/&#536;/g, 'Ș')
      .replace(/&#539;/g, 'ț')
      .replace(/&#538;/g, 'Ț')
      .replace(/â€™/g, "'")
      .replace(/â€œ/g, '"')
      .replace(/â€/g, '"')
      .replace(/â€"/g, '—')
      .replace(/â€"/g, '–');
  };

  const normalizePlainTextBlock = (block: string) => {
    const lines = block
      .split('\n')
      .map(line => line.replace(/[ \t]+/g, ' ').trim())
      .filter(Boolean);

    if (lines.length === 0) {
      return '';
    }

    const isHeadingBlock =
      lines.length <= 3 &&
      lines.every(line => line.length <= 70) &&
      lines.every(line =>
        /^(CAPITOLUL|CHAPTER|PARTEA|PARTE|ACTUL|SCENA)\b/i.test(line) ||
        /^[A-ZĂÂÎȘȚ0-9 .,:;!?'"()\-]+$/.test(line)
      );

    const isDialogueBlock = lines.every(line => /^[—–-]\s*/.test(line));
    const shortLineCount = lines.filter(line => line.length <= 55).length;
    const isVerseBlock = !isDialogueBlock && lines.length >= 3 && shortLineCount / lines.length >= 0.7;

    if (isHeadingBlock || isDialogueBlock || isVerseBlock) {
      return lines.join('\n');
    }

    return lines.join(' ').replace(/\s+([,.;:!?])/g, '$1');
  };

  const preprocessPlainTextContent = (content: string) => {
    const normalizedContent = decodeTextEntities(
      content.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    );

    return normalizedContent
      .split(/\n{2,}/)
      .map(block => normalizePlainTextBlock(block))
      .filter(Boolean)
      .join('\n\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  // Fetch book content from API
  const fetchBookContent = async () => {
    try {
      setLoading(true);
      
      // Dacă cartea are deja conținut, îl procesăm
      if (book.content) {
        const detectedFormat = detectContentFormat(book.fileName, book.fileType);
        const processedContent = preprocessBookContent(book.content, detectedFormat);
        setContentFormat(detectedFormat);
        setBookContent(processedContent);
        setTotalPages(Math.ceil(processedContent.length / 800));
        return;
      }
      
      if (!token || isTokenExpired(token)) {
        setBookContent(generateFallbackContent());
        setTotalPages(50);
        return;
      }

      const response = await fetch(buildApiUrl(`/books/${book.id}/content`), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Book content received:', data);
        
        if (data.content) {
          const detectedFormat = detectContentFormat(book.fileName, book.fileType, data.format);
          const processedContent = preprocessBookContent(data.content, detectedFormat);
          setContentFormat(detectedFormat);
          setBookContent(processedContent);
          setBookLanguage(data.language || 'en');
          setTotalPages(Math.ceil(processedContent.length / 800));
        } else {
          setBookContent(generateFallbackContent());
          setTotalPages(50);
        }
      } else if (response.status === 401) {
        logout();
        setBookContent(generateFallbackContent());
        setTotalPages(50);
      } else {
        setBookContent(generateFallbackContent());
        setTotalPages(50);
      }
    } catch (error) {
      console.error('Error fetching book content:', error);
      setBookContent(generateFallbackContent());
      setTotalPages(50);
    } finally {
      setLoading(false);
    }
  };

  // Funcție pentru generarea conținutului de rezervă
  const generateFallbackContent = () => {
    // Generează conținut realist cu excerpts literare și structură de carte
    const excerpts = [
      // Prima pagină - informații despre carte
      `<div class="chapter-title" style="font-size: ${fontSize + 4}px; font-weight: bold; text-align: center; margin: 2rem 0; text-transform: uppercase; letter-spacing: 1px;">${book.title}</div>
      <div style="text-align: center; margin: 1rem 0; font-style: italic;">de ${book.author}</div>
      <div style="text-align: center; margin: 2rem 0;">———————————————</div>
      <p style="margin: 1em 0; text-align: justify; line-height: 1.6; text-indent: 2em;">
        <span style="font-size: ${fontSize + 8}px; font-weight: bold; float: left; line-height: 1; margin: 0 0.1em 0 0;">A</span>ceastă carte vă invită într-o călătorie fascinantă prin universul cuvintelor și al imaginației. Fiecare pagină dezvăluie o nouă dimensiune a poveștii, conectând cititorii cu personajele și aventurile lor extraordinare.
      </p>
      <p style="margin: 1em 0; text-align: justify; line-height: 1.6; text-indent: 2em;">Prin lectura acestei opere, veți descoperi nu doar o narațiune captivantă, ci și reflecții profunde asupra naturii umane și asupra lumii în care trăim.</p>`,
      
      // Pagina 2 - începutul poveștii
      `<div class="chapter-title" style="font-size: ${fontSize + 4}px; font-weight: bold; text-align: center; margin: 2rem 0; text-transform: uppercase; letter-spacing: 1px;">CAPITOLUL I</div>
      <p style="margin: 1em 0; text-align: justify; line-height: 1.6; text-indent: 2em;">
        <span style="font-size: ${fontSize + 8}px; font-weight: bold; float: left; line-height: 1; margin: 0 0.1em 0 0;">Î</span>n zori de zi, când lumina pătrundea timid prin ferestrele înguste, orașul se trezea încet la viață. Străzile încă pustii îşi aşteptau trecătorii, iar cafeaua aburea în ceștile acelora care se pregăteau pentru o nouă zi plină de provocări și descoperiri.
      </p>
      <p style="margin: 1em 0; text-align: justify; line-height: 1.6; text-indent: 2em;">Protagonistul nostru, un tânăr plin de vise și speranțe, privea pe fereastră observând cum lumea se schimbă în fața ochilor săi. Era începutul unei aventuri care avea să-i transforme complet existența.</p>
      <div class="dialogue-paragraph" style="margin: 0.8em 0; text-indent: 1em; position: relative;">
        <span class="dialogue-marker" style="font-weight: bold; color: #4F46E5;">—</span>
        <span class="dialogue-text">Astăzi va fi o zi diferită</span>, își spuse el în gând, simțind cum inima îi bătea mai tare de emoție.
      </div>`,
      
      // Pagina 3 - dezvoltarea acțiunii
      `<p style="margin: 1em 0; text-align: justify; line-height: 1.6; text-indent: 2em;">Pașii lui răsunau pe pietrele străzii vechi, fiecare pas marcând distanța dintre ceea ce fusese și ceea ce avea să devină. Orașul își dezvăluia tainele încet, ca un prieten care-și împărțește secretele doar cu cei aleși.</p>
      <p style="margin: 1em 0; text-align: justify; line-height: 1.6; text-indent: 2em;">În departare se auzeau sunete cunoscute: clopotele bisericii vecine, țipetele vânzătorilor de pe piață, râsul copiilor care se îndreptau spre școală. Era simfonia matinală a unei comunități care trăia și respira în același ritm de veacuri întregi.</p>
      <div class="dialogue-paragraph" style="margin: 0.8em 0; text-indent: 1em; position: relative;">
        <span class="dialogue-marker" style="font-weight: bold; color: #4F46E5;">„</span>
        <span class="dialogue-text">Unde să merg întâi?</span>"
      </div>
      <p style="margin: 1em 0; text-align: justify; line-height: 1.6; text-indent: 2em;">se întrebă el, oprindu-se la intersecția care ducea spre trei direcții diferite. Fiecare drum promitea aventuri și întâlniri neașteptate.</p>`,
      
      // Pagina 4 - continuarea
      `<p style="margin: 1em 0; text-align: justify; line-height: 1.6; text-indent: 2em;">Alegerea nu a fost grea. Instinctul îl îndrupta spre drumul din stânga, cel care ducea către cartierul vechi al orașului, unde fiecare clădire păstra povești din timpurile de demult. Acolo, în labrintul de străduțe înguste și piețe pitice, se ascundeau cele mai frumoase surprize.</p>
      <p style="margin: 1em 0; text-align: justify; line-height: 1.6; text-indent: 2em;">Pe măsură ce înaintează, peisajul se schimba subtil. Casele moderne lăsau locul unor construcții mai vechi, cu ziduri de piatră și acoperișuri de țiglă roșie care strălucea în lumina dimineții. Aici, timpul părea să fi stat în loc, păstrând farmecul și autenticitatea trecutului.</p>
      <div class="dialogue-paragraph" style="margin: 0.8em 0; text-indent: 1em; position: relative;">
        <span class="dialogue-marker" style="font-weight: bold; color: #4F46E5;">—</span>
        <span class="dialogue-text">Bună dimineața, tinerule!</span>
      </div>
      <p style="margin: 1em 0; text-align: justify; line-height: 1.6; text-indent: 2em;">îl salută o bătrânică care îngrijea florile de la fereastra sa.</p>`,
      
      // Pagina 5 - întâlniri și descoperiri
      `<div class="chapter-title" style="font-size: ${fontSize + 4}px; font-weight: bold; text-align: center; margin: 2rem 0; text-transform: uppercase; letter-spacing: 1px;">CAPITOLUL II</div>
      <p style="margin: 1em 0; text-align: justify; line-height: 1.6; text-indent: 2em;">
        <span style="font-size: ${fontSize + 8}px; font-weight: bold; float: left; line-height: 1; margin: 0 0.1em 0 0;">I</span>ntâlnirea cu bătrânica avea să se dovedească mai importantă decât își imagina. În ochii ei blajini se citea înțelepciunea anilor și experiența unei vieți trăite din plin. Era exact tipul de persoană de la care se pot învăța cele mai prețioase lecții.
      </p>
      <div class="dialogue-paragraph" style="margin: 0.8em 0; text-indent: 1em; position: relative;">
        <span class="dialogue-marker" style="font-weight: bold; color: #4F46E5;">„</span>
        <span class="dialogue-text">Vii pe aici pentru prima dată?</span>" întrebă ea, cu un zâmbet călduros care îi lumină întreaga față.
      </div>
      <div class="dialogue-paragraph" style="margin: 0.8em 0; text-indent: 1em; position: relative;">
        <span class="dialogue-marker" style="font-weight: bold; color: #4F46E5;">„</span>
        <span class="dialogue-text">Da, și sunt fascinat de frumusețea acestui loc</span>", răspunse el cu sinceritate.
      </div>`,
    ];
    
    // Calculează indexul excerpt-ului bazat pe pagina curentă
    const excerptIndex = (currentPage - 1) % excerpts.length;
    return excerpts[excerptIndex];
  };

  const preprocessBookContent = (
    content: string,
    format: 'txt' | 'epub' | 'pdf' | 'unknown' = contentFormat
  ) => {
    if (format === 'txt') {
      return preprocessPlainTextContent(content);
    }

    const normalizedContent = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const containsHtml = /<[^>]+>/.test(normalizedContent);

    let cleanedContent = containsHtml
      ? normalizedContent
          // Preserve block semantics before stripping tags
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<\/p>/gi, '\n\n')
          .replace(/<\/div>/gi, '\n\n')
          .replace(/<\/section>/gi, '\n\n')
          .replace(/<\/article>/gi, '\n\n')
          .replace(/<\/blockquote>/gi, '\n\n')
          .replace(/<\/h[1-6]>/gi, '\n\n')
          .replace(/<\/li>/gi, '\n')
          .replace(/<li[^>]*>/gi, '- ')
          // Remove all remaining HTML tags
          .replace(/<[^>]*>/g, '')
      : normalizedContent;

    cleanedContent = decodeTextEntities(cleanedContent)
      // Preserve paragraph structure for TXT content
      .replace(/[ \t\f\v]+/g, ' ')
      .replace(/ *\n */g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    // Now format the cleaned content
    return cleanedContent
      // Improve dialogue formatting
      .replace(/([.!?])\s*([—-])\s*([A-ZĂÂÎȘȚ])/g, '$1\n\n— $3')
      .replace(/^([—-])\s*/gm, '— ')
      .replace(/([.!?])\s*([„"])/g, '$1\n\n$2')
      // Improve chapter formatting
      .replace(/^(CAPITOLUL|Capitolul|CHAPTER|Chapter)\s+(\d+|[IVXLC]+)/gm, '\n\n$1 $2\n')
      .replace(/^([A-ZĂÂÎȘȚ\s]{5,50})$/gm, '\n\n$1\n')
      // Ensure proper paragraph spacing
      .replace(/([.!?])\s*([A-ZĂÂÎȘȚ])/g, '$1\n\n$2')
      // Clean up multiple line breaks
      .replace(/\n{3,}/g, '\n\n')
      .replace(/[ \t]+/g, ' ')
      .trim();
  };

  // Fetch book content from API
  useEffect(() => {
    fetchBookContent();
  }, [book.id, book.content]);

  // Fetch user annotations for this book
  useEffect(() => {
    const fetchAnnotations = async () => {
      if (!token || !book.id || isTokenExpired(token)) {
        setAnnotations([]);
        return;
      }
      
      try {
        const bookAnnotations = await AnnotationsService.getAnnotations(book.id, token);
        if (bookAnnotations.length > 0) {
          setAnnotations(bookAnnotations as Annotation[]);
        }
      } catch (error) {
        console.error('Failed to fetch annotations:', error);
      }
    };
    
    fetchAnnotations();
  }, [book.id, token]);

  // Calculează numărul total de pagini bazat pe conținut
  useEffect(() => {
    if (bookContent) {
      // Estimăm numărul de caractere pe pagină bazat pe dimensiunea fontului
      const charsPerPage = 3000 / (fontSize / 16);
      const contentLength = bookContent.length;
      
      // Calculăm numărul total de pagini
      const calculatedPages = Math.max(1, Math.ceil(contentLength / charsPerPage));
      setTotalPages(calculatedPages);
    }
  }, [bookContent, fontSize]);

  // Modify the generatePageContent function to return plain text instead of HTML
  const generatePageContent = useMemo(() => {
    return (pageNumber: number, realContent: string) => {
      if (!realContent || realContent.trim().length === 0) {
        return { pages: [generateFallbackPlainText(pageNumber)], totalPages: 1 };
      }
      
      // Preprocess content for better formatting
      const processedContent = preprocessBookContent(realContent);
      
      // Improved character calculation based on font size and container
      const baseCharsPerPage = 2200; // Reduced base for better page distribution
      const fontMultiplier = 16 / fontSize; // Adjust based on current font size
      const charsPerPage = Math.floor(baseCharsPerPage * fontMultiplier);
      const minPageContent = Math.floor(charsPerPage * 0.3); // Minimum 30% of page content
      
      // Split content into paragraphs
      const paragraphs = processedContent.split(/\n\s*\n/).filter(p => p.trim().length > 0);
      
      const pages = [];
      let currentPage = '';
      let currentPageLength = 0;
      
      for (let i = 0; i < paragraphs.length; i++) {
        const paragraph = paragraphs[i].trim();
        if (!paragraph) continue;
        
        // Check if this is a chapter title
        const isChapterTitle = /^(CAPITOLUL|CHAPTER|CAP\.|Part|Partea)\s+[\dIVX]+/i.test(paragraph) ||
                              /^[A-ZĂÂÎȘȚ\s]{5,50}$/i.test(paragraph) ||
                              (paragraph.length < 100 && paragraph.split(' ').length < 10 && paragraph === paragraph.toUpperCase());
        
        // If it's a chapter title and we have sufficient content, start new page
        if (isChapterTitle && currentPage.trim() && currentPageLength >= minPageContent) {
          pages.push(currentPage.trim());
          currentPage = '';
          currentPageLength = 0;
        }
        
        const paragraphWithSpacing = paragraph + '\n\n';
        
        // Check if adding this paragraph would exceed page limit
        if (currentPageLength + paragraphWithSpacing.length > charsPerPage && currentPage.trim()) {
          // Only split if current page has enough content
          if (currentPageLength >= minPageContent) {
            // If paragraph is very long, try to split it at sentence boundaries
            if (paragraphWithSpacing.length > charsPerPage * 0.7) {
              const sentences = paragraph.split(/(?<=[.!?])\s+/);
              let tempParagraph = '';
              
              for (const sentence of sentences) {
                const sentenceWithSpace = sentence.trim() + ' ';
                
                // Check if adding this sentence would exceed the limit
                if (currentPageLength + tempParagraph.length + sentenceWithSpace.length > charsPerPage) {
                  // Only create new page if we have sufficient content
                  if (currentPageLength + tempParagraph.length >= minPageContent) {
                    if (tempParagraph.trim()) {
                      currentPage += tempParagraph.trim() + '\n\n';
                    }
                    pages.push(currentPage.trim());
                    currentPage = '';
                    currentPageLength = 0;
                    tempParagraph = '';
                  }
                }
                
                tempParagraph += sentenceWithSpace;
              }
              
              // Add remaining content
              if (tempParagraph.trim()) {
                currentPage += tempParagraph.trim() + '\n\n';
                currentPageLength = currentPage.length;
              }
            } else {
              // If paragraph is not too long, start new page only if current page has enough content
              pages.push(currentPage.trim());
              currentPage = paragraphWithSpacing;
              currentPageLength = paragraphWithSpacing.length;
            }
          } else {
            // If current page doesn't have enough content, just add the paragraph
            currentPage += paragraphWithSpacing;
            currentPageLength += paragraphWithSpacing.length;
          }
        } else {
          // Add paragraph to current page
          currentPage += paragraphWithSpacing;
          currentPageLength += paragraphWithSpacing.length;
        }
      }
      
      // Add the last page if it has content
      if (currentPage.trim()) {
        pages.push(currentPage.trim());
      }
      
      // Post-process pages to merge very short pages
      const processedPages: string[] = [];
      for (let i = 0; i < pages.length; i++) {
        const page: string = pages[i];
        const wordCount = page.split(/\s+/).length;
        
        // If page is too short and not the last page, try to merge with next
        if (wordCount < 20 && i < pages.length - 1) {
          const nextPage: string = pages[i + 1];
          const combinedContent: string = page + '\n\n' + nextPage;
          
          // If combined content is not too long, merge them
          if (combinedContent.length <= charsPerPage * 1.2) {
            pages[i + 1] = combinedContent;
            continue; // Skip adding this page as it's merged with the next
          }
        }
        
        processedPages.push(page);
      }
      
      // Ensure we have at least one page
      if (processedPages.length === 0) {
        processedPages.push(generateFallbackPlainText(1));
      }
      
      return { pages: processedPages, totalPages: processedPages.length };
    };
  }, [fontSize, bookContent]);

  // Generate page content and handle pagination
  const { content: pageContent, totalPagesCount } = useMemo(() => {
    const content = bookContent || generateFallbackContent();
    const result = generatePageContent(currentPage, content);
    
    const pageIndex = currentPage - 1;
    if (pageIndex >= 0 && pageIndex < result.pages.length) {
      return {
        content: result.pages[pageIndex],
        totalPagesCount: result.totalPages
      };
    }
    return {
      content: result.pages[result.pages.length - 1] || generateFallbackPlainText(currentPage),
      totalPagesCount: result.totalPages
    };
  }, [currentPage, bookContent, generatePageContent]);

  // Update total pages when content changes
  useEffect(() => {
    if (totalPagesCount !== totalPages) {
      setTotalPages(totalPagesCount);
    }
  }, [totalPagesCount, totalPages]);

  // New function to generate plain text fallback content
  const generateFallbackPlainText = (pageNumber: number) => {
    const excerpts = [
      // Prima pagină
      `${book.title}\n\nde ${book.author}\n\n———————————————\n\nAceastă carte vă invită într-o călătorie fascinantă prin universul cuvintelor și al imaginației. Fiecare pagină dezvăluie o nouă dimensiune a poveștii, conectând cititorii cu personajele și aventurile lor extraordinare.\n\nPrin lectura acestei opere, veți descoperi nu doar o narațiune captivantă, ci și reflecții profunde asupra naturii umane și asupra lumii în care trăim.`,
      
      // Pagina 2
      `CAPITOLUL I\n\nÎn zori de zi, când lumina pătrundea timid prin ferestrele înguste, orașul se trezea încet la viață. Străzile încă pustii îşi aşteptau trecătorii, iar cafeaua aburea în ceștile acelora care se pregăteau pentru o nouă zi plină de provocări și descoperiri.\n\nProtagonistul nostru, un tânăr plin de vise și speranțe, privea pe fereastră observând cum lumea se schimbă în fața ochilor săi. Era începutul unei aventuri care avea să-i transforme complet existența.\n\n— Astăzi va fi o zi diferită, își spuse el în gând, simțind cum inima îi bătea mai tare de emoție.`,
      
      // Pagina 3
      `Pașii lui răsunau pe pietrele străzii vechi, fiecare pas marcând distanța dintre ceea ce fusese și ceea ce avea să devină. Orașul își dezvăluia tainele încet, ca un prieten care-și împărțește secretele doar cu cei aleși.\n\nÎn departare se auzeau sunete cunoscute: clopotele bisericii vecine, țipetele vânzătorilor de pe piață, râsul copiilor care se îndreptau spre școală. Era simfonia matinală a unei comunități care trăia și respira în același ritm de veacuri întregi.\n\n„Unde să merg întâi?" se întrebă el, oprindu-se la intersecția care ducea spre trei direcții diferite. Fiecare drum promitea aventuri și întâlniri neașteptate.`,
      
      // Pagina 4
      `Alegerea nu a fost grea. Instinctul îl îndrupta spre drumul din stânga, cel care ducea către cartierul vechi al orașului, unde fiecare clădire păstra povești din timpurile de demult. Acolo, în labrintul de străduțe înguste și piețe pitice, se ascundeau cele mai frumoase surprize.\n\nPe măsură ce înaintează, peisajul se schimba subtil. Casele moderne lăsau locul unor construcții mai vechi, cu ziduri de piatră și acoperișuri de țiglă roșie care strălucea în lumina dimineții. Aici, timpul părea să fi stat în loc, păstrând farmecul și autenticitatea trecutului.\n\n— Bună dimineața, tinerule! îl salută o bătrânică care îngrijea florile de la fereastra sa.`,
      
      // Pagina 5
      `CAPITOLUL II\n\nIntâlnirea cu bătrânica avea să se dovedească mai importantă decât își imagina. În ochii ei blajini se citea înțelepciunea anilor și experiența unei vieți trăite din plin. Era exact tipul de persoană de la care se pot învăța cele mai prețioase lecții.\n\n„Vii pe aici pentru prima dată?" întrebă ea, cu un zâmbet călduros care îi lumină întreaga față.\n\n„Da, și sunt fascinat de frumusețea acestui loc", răspunse el cu sinceritate.`,
    ];
    
    const excerptIndex = (pageNumber - 1) % excerpts.length;
    return excerpts[excerptIndex];
  };

  // Get styles based on reading mode
  const getReadingModeStyles = () => {
    if (readingMode === 'night') {
      return {
        dialogueLine: {
          ...epubStyles.dialogueLine,
          borderLeft: '2px solid rgba(150, 150, 150, 0.25)',
        },
        dialogueMarker: {
          ...epubStyles.dialogueMarker,
          color: 'rgba(200, 200, 200, 0.9)',
        }
      };
    }
    return {
      dialogueLine: epubStyles.dialogueLine,
      dialogueMarker: epubStyles.dialogueMarker
    };
  };

  const readingStyles = getReadingModeStyles();

  // Navigation functions
  const goToPrevPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      
      // Scroll to top when changing pages
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      
      // Scroll to top when changing pages
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    }
  };

  const goToPage = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      
      // Scroll to top when changing pages
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    }
  };

  // Go to specific annotation's page
  const goToAnnotation = (annotation: Annotation) => {
    goToPage(annotation.position.page);
    // TODO: Implement scrolling to the specific text position
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const changeFontSize = (newSize: number) => {
    setFontSize(Math.max(12, Math.min(24, newSize)));
  };

  const startReading = () => {
    setViewMode('read');
  };

  // Reading mode and annotation tools
  const toggleReadingMode = () => {
    setReadingMode(prev => prev === 'day' ? 'night' : 'day');
  };

  const toggleAnnotationTool = (tool: 'highlight' | 'note' | 'bookmark' | null) => {
    setActiveAnnotationTool(prev => prev === tool ? null : tool);
  };

  // Enhanced text selection handling
  const handleTextSelection = () => {
    if (!activeAnnotationTool || !window.getSelection || !token) return;
    
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const selectedText = selection.toString().trim();
    if (selectedText === '') return;
    
    setSelectedText(selectedText);
    
    // Get the selection range
    const range = selection.getRangeAt(0);
    
    // Calculate offsets relative to the content container
    let startOffset = 0;
    let endOffset = selectedText.length;
    
    // Try to get more accurate offsets if possible
    if (contentRef.current) {
      const contentEl = contentRef.current;
      const contentText = contentEl.textContent || '';
      const selectionIndex = contentText.indexOf(selectedText);
      if (selectionIndex !== -1) {
        startOffset = selectionIndex;
        endOffset = startOffset + selectedText.length;
      }
    }
    
    if (activeAnnotationTool === 'highlight') {
      addHighlight(selectedText, startOffset, endOffset);
      selection.removeAllRanges();
    } else if (activeAnnotationTool === 'note') {
      // Store the selected text and position temporarily
      setTempAnnotation({ text: selectedText, startOffset, endOffset });
      // Open note dialog
      setShowNoteDialog(true);
      selection.removeAllRanges();
    } else if (activeAnnotationTool === 'bookmark') {
      addBookmark(startOffset, endOffset);
      selection.removeAllRanges();
    }
  };

  // Add a highlight annotation
  const addHighlight = async (text: string, startOffset: number, endOffset: number) => {
    if (!token || !book.id) return;
    
    try {
      const result = await AnnotationsService.createHighlight(
        book.id,
        currentPage,
        text,
        highlightColor,
        { startOffset, endOffset },
        token
      );
      
      if (result) {
    const newAnnotation: Annotation = {
          id: result.id,
      type: 'highlight',
      content: text,
      color: highlightColor,
      position: {
        startOffset,
        endOffset,
        page: currentPage
      },
      createdAt: new Date()
    };
    
    setAnnotations(prev => [...prev, newAnnotation]);
      }
    } catch (error) {
      console.error('Failed to create highlight:', error);
    }
  };

  // Add a note annotation
  const addNote = async (text: string, startOffset: number, endOffset: number, noteContent: string) => {
    if (!token || !book.id) return;
    
    try {
      const result = await AnnotationsService.createNote(
        book.id,
        currentPage,
        noteContent,
        { startOffset, endOffset },
        token
      );
      
      if (result) {
    const newAnnotation: Annotation = {
          id: result.id,
      type: 'note',
      content: noteContent,
      position: {
        startOffset,
        endOffset,
        page: currentPage
      },
      createdAt: new Date()
    };
    
    setAnnotations(prev => [...prev, newAnnotation]);
      }
      
      // Reset note states
      setNoteContent('');
      setShowNoteDialog(false);
      setTempAnnotation(null);
    } catch (error) {
      console.error('Failed to create note:', error);
    }
  };

  // Handle submitting a note from the dialog
  const handleNoteSubmit = () => {
    if (tempAnnotation && noteContent.trim()) {
      addNote(
        tempAnnotation.text,
        tempAnnotation.startOffset,
        tempAnnotation.endOffset,
        noteContent
      );
    } else {
      setShowNoteDialog(false);
      setTempAnnotation(null);
    }
  };

  // Add a bookmark annotation
  const addBookmark = async (startOffset: number, endOffset: number) => {
    if (!token || !book.id) return;
    
    try {
      const result = await AnnotationsService.createBookmark(
        book.id,
        currentPage,
        `Pagina ${currentPage}`,
        undefined,
        token
      );
      
      if (result) {
    const newAnnotation: Annotation = {
          id: result.id,
      type: 'bookmark',
      position: {
        startOffset,
        endOffset,
        page: currentPage
      },
      createdAt: new Date()
    };
    
    setAnnotations(prev => [...prev, newAnnotation]);
      }
    } catch (error) {
      console.error('Failed to create bookmark:', error);
    }
  };

  // Remove an annotation
  const removeAnnotation = async (id: string | number) => {
    if (!token) return;
    
    try {
      // Get the annotation to determine its type
      const annotation = annotations.find(ann => ann.id === id);
      if (!annotation) return;
      
      let success = false;
      
      if (annotation.type === 'bookmark') {
        success = await AnnotationsService.deleteBookmark(id, token);
      } else {
        // Both highlights and notes are stored as notes
        success = await AnnotationsService.deleteNote(id, token);
      }
      
      if (success) {
    setAnnotations(prev => prev.filter(ann => ann.id !== id));
      }
    } catch (error) {
      console.error('Failed to delete annotation:', error);
    }
  };

  const toggleAnnotationsPanel = () => {
    setShowAnnotationsPanel(prev => !prev);
  };

  // Text-to-Speech functionality
  useEffect(() => {
    if (typeof window !== 'undefined') {
      speechSynthesisRef.current = window.speechSynthesis;
    }
    
    return () => {
      if (speechSynthesisRef.current && speechUtteranceRef.current) {
        speechSynthesisRef.current.cancel();
      }
    };
  }, []);

  const toggleTextToSpeech = () => {
    if (!speechSynthesisRef.current) return;
    
    if (isTextToSpeechActive) {
      speechSynthesisRef.current.cancel();
      setIsTextToSpeechActive(false);
      return;
    }
    
    if (contentRef.current) {
      const text = contentRef.current.textContent || '';
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = /^ro\b/i.test(bookLanguage) ? 'ro-RO' : 'en-US';
      
      speechUtteranceRef.current = utterance;
      speechSynthesisRef.current.speak(utterance);
      setIsTextToSpeechActive(true);
      
      utterance.onend = () => {
        setIsTextToSpeechActive(false);
      };
    }
  };

  // Save to library handler
  const handleSaveToLibrary = () => {
    // Implement the logic to save the book to the library
    console.log("Saving book to library");
  };

  // Dismiss welcome message
  const dismissWelcomeMessage = () => {
    setShowWelcomeMessage(false);
  };

  // Convert any ID to string to avoid type errors
  const idToString = (id: string | number): string => {
    return String(id);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden flex justify-center items-center bg-black bg-opacity-75">
      <div 
        className={`w-full h-full flex flex-col ${
          readingMode === 'night' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-800'
        }`}
      >
        {/* Welcome message overlay */}
        {showWelcomeMessage && currentPage === 1 && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-70">
            <div className={`max-w-xl p-6 rounded-lg shadow-xl ${
              readingMode === 'night' ? 'bg-gray-800' : 'bg-white'
            }`}>
              <h3 className="text-xl font-bold mb-4">Bine ați venit la cititor!</h3>
              <div className="space-y-4">
                <p>Acesta este cititorul de cărți al Bibliotecii Digitale Virtuale. Iată câteva funcții care vă pot ajuta:</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Folosiți butoanele de navigare pentru a schimba paginile</li>
                  <li>Ajustați dimensiunea textului cu butoanele + și -</li>
                  <li>Comutați între modul zi și noapte pentru confort vizual</li>
                  <li>Selectați text pentru a-l evidenția sau adăuga notițe</li>
                  <li>Accesați panoul de adnotări pentru a vedea toate notele dvs.</li>
                </ul>
                <p>Lectură plăcută!</p>
          </div>
              <div className="mt-6 flex justify-end">
                <button 
                  onClick={dismissWelcomeMessage}
                  className={`px-4 py-2 rounded ${
                    readingMode === 'night' 
                      ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  Începe lectura
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Note dialog overlay */}
        {showNoteDialog && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-70">
            <div className={`max-w-md w-full p-6 rounded-lg shadow-xl ${
              readingMode === 'night' ? 'bg-gray-800' : 'bg-white'
            }`}>
              <h3 className="text-xl font-bold mb-4">Adaugă o notă</h3>
              <div>
                {tempAnnotation && (
                  <div className="mb-4 p-3 bg-gray-100 rounded-md text-gray-800">
                    <p className="text-sm italic">{tempAnnotation.text}</p>
                  </div>
                )}
                <textarea
                  value={noteContent}
                  onChange={e => setNoteContent(e.target.value)}
                  placeholder="Scrie nota ta aici..."
                  className="w-full p-3 border rounded-md text-gray-800 focus:ring-2 focus:ring-blue-500"
                  rows={4}
                ></textarea>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button 
                  onClick={() => {
                    setShowNoteDialog(false);
                    setTempAnnotation(null);
                    setNoteContent('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Anulează
                </button>
                <button 
                  onClick={handleNoteSubmit}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  disabled={!noteContent.trim()}
                >
                  Salvează
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className={`px-6 py-3 flex justify-between items-center shadow-md ${
          readingMode === 'night' ? 'bg-gray-800' : 'bg-blue-600 text-white'
        }`}>
          <div className="flex items-center">
            <h2 className="text-lg font-semibold truncate mr-2">{book.title}</h2>
            <span className="text-sm opacity-75">de {book.author}</span>
          </div>
          <div className="flex items-center space-x-4">
                <button 
              onClick={toggleAnnotationsPanel}
              className={`p-2 rounded ${
                showAnnotationsPanel 
                  ? 'bg-white bg-opacity-20' 
                  : 'hover:bg-white hover:bg-opacity-10'
              }`}
              aria-label="Adnotări"
            >
              <AnnotationIcon />
                </button>
            <button 
              onClick={toggleFullscreen}
              className="p-2 rounded hover:bg-white hover:bg-opacity-10"
              aria-label={isFullscreen ? 'Ieșire din modul fullscreen' : 'Intrare în modul fullscreen'}
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={
                  isFullscreen 
                    ? "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    : "M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                } />
                </svg>
            </button>
            <button 
              onClick={onClose}
              className="p-2 rounded hover:bg-white hover:bg-opacity-10"
              aria-label="Închide"
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Main content with overflow handling */}
        <div className="flex-1 overflow-hidden relative">
          {/* Loading indicator */}
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          )}
          
          {/* Error message */}
          {error && (
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 max-w-md bg-red-100 p-4 rounded-md text-red-800 z-40">
              <p>{error}</p>
            </div>
          )}
          
          {/* Book container with flex layout */}
          <div className="flex h-full">
            {/* Main reading area */}
            <div className={`flex-1 overflow-auto px-4 py-8 ${showAnnotationsPanel ? 'mr-80' : ''} transition-all duration-300`}>
              {/* Book content */}
              <div 
                className={`max-w-4xl mx-auto shadow-lg rounded-lg p-8 min-h-full ${
                  readingMode === 'night' ? 'bg-gray-900 text-gray-200' : 'bg-white text-gray-800'
                }`}
              >
                <div 
                  ref={contentRef}
                  style={{ 
                    fontSize: `${fontSize}px`,
                    ...epubStyles.epubContent
                  }} 
                  className="epub-reader-content"
                  onMouseUp={handleTextSelection}
                >
                  <p className={`text-center text-sm mb-6 ${readingMode === 'night' ? 'text-gray-400' : 'text-gray-400'}`}>
                    Pagina {currentPage} din {totalPages}
                  </p>
                  {currentPage === 1 && (
                    <div className="mb-12 text-center">
                      <h2 className="text-2xl mb-8">{book.title}</h2>
                      <p className="text-xl mb-2">de</p>
                      <h3 className="text-xl mb-16">{book.author}</h3>
                </div>
                  )}
                  
                  {/* Process the content with proper React components */}
                  <div className="epub-formatted-content">
                    {pageContent.split('\n\n').map((paragraph: string, index: number) => {
                      const trimmedParagraph = paragraph.trim();
                      if (!trimmedParagraph) return null;
                      
                      // Check if paragraph is a chapter title
                      const isChapterTitle = /^(CAPITOLUL|CHAPTER|CAP\.|Part|Partea)\s+[\dIVX]+/i.test(trimmedParagraph) ||
                                           /^[A-ZĂÂÎȘȚ\s]{5,50}$/i.test(trimmedParagraph) ||
                                           (trimmedParagraph.length < 100 && trimmedParagraph.split(' ').length < 10 && trimmedParagraph === trimmedParagraph.toUpperCase());
                      
                      // Check if paragraph contains dialogue
                      const hasDialogue = /(^|\n)\s*[—–-]\s+/.test(trimmedParagraph);
                      
                      // Render chapter title
                      if (isChapterTitle) {
                        return (
                          <div 
                            key={index} 
                            className="chapter-title text-center font-bold uppercase tracking-wider mb-8 mt-8"
                            style={{ 
                              fontSize: `${fontSize + 4}px`,
                              letterSpacing: '1px',
                              textTransform: 'uppercase'
                            }}
                          >
                            {trimmedParagraph}
                </div>
                        );
                      }
                      
                      // Render paragraph with dialogue
                      if (hasDialogue) {
                        const lines = trimmedParagraph
                          .split('\n')
                          .map(line => line.trim())
                          .filter(Boolean);
                        
                        return (
                          <div key={index} className="mb-4 leading-relaxed" style={{ textAlign: 'justify' }}>
                            {lines.map((line: string, lineIndex: number) => {
                              const dialogueMatch = line.match(/^([—–-])\s*(.*)$/);
                              
                              if (dialogueMatch) {
                                const [, marker, text] = dialogueMatch;
                                return (
                                  <span key={lineIndex} className="dialogue-line">
                                    <span
                                      className="dialogue-marker font-bold"
                                      style={{ color: readingMode === 'night' ? 'rgba(200, 200, 200, 0.9)' : '#4F46E5' }}
                                    >
                                      {marker}{' '}
                                    </span>
                                    <span>{text}</span>
                                  </span>
                                );
                              }

                              return (
                                <p
                                  key={lineIndex}
                                  className="mb-2 leading-relaxed"
                                  style={{ textAlign: 'justify', textIndent: '2em', whiteSpace: 'pre-line' }}
                                >
                                  {line}
                                </p>
                              );
                            })}
                          </div>
                        );
                      }
                      
                      // Check if this is the first paragraph after a chapter title for drop cap
                      const prevParagraph = index > 0 ? pageContent.split('\n\n')[index - 1] : '';
                      const isPrevChapterTitle = /^(CAPITOLUL|CHAPTER|CAP\.|Part|Partea)\s+[\dIVX]+/i.test(prevParagraph) ||
                                               /^[A-ZĂÂÎȘȚ\s]{5,50}$/i.test(prevParagraph);
                      
                      // Regular paragraph with potential drop cap
                      if (isPrevChapterTitle && trimmedParagraph.length > 30) {
                        const firstLetter = trimmedParagraph.charAt(0);
                        const restOfText = trimmedParagraph.slice(1);
                        
                        return (
                          <p key={index} className="mb-4 leading-relaxed" style={{ textAlign: 'justify', textIndent: '2em' }}>
                            <span 
                              className="first-letter font-bold float-left mr-1"
                              style={{ 
                                fontSize: `${fontSize + 12}px`, 
                                lineHeight: '1',
                                marginTop: '0.1em'
                              }}
                            >
                              {firstLetter}
                            </span>
                            {restOfText}
                          </p>
                        );
                      }
                      
                      // Regular paragraph
                      return (
                        <p 
                          key={index} 
                          className="mb-4 leading-relaxed"
                          style={{ textAlign: 'justify', textIndent: '2em', whiteSpace: 'pre-line' }}
                        >
                          {trimmedParagraph}
                        </p>
                      );
                    }).filter(Boolean)}
              </div>
            </div>
          </div>
            </div>

            {/* Annotations sidebar - Fixed position on the right */}
            {showAnnotationsPanel && (
              <div className={`fixed right-0 top-0 w-80 h-full shadow-lg border-l z-40 overflow-auto transition-transform duration-300 transform ${
                showAnnotationsPanel ? 'translate-x-0' : 'translate-x-full'
              } ${readingMode === 'night' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className="p-4 h-full flex flex-col">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className={`font-bold text-lg ${readingMode === 'night' ? 'text-white' : 'text-gray-800'}`}>
                      Adnotări
                    </h3>
                    <button 
                      onClick={toggleAnnotationsPanel}
                      className={`p-1 rounded-full ${readingMode === 'night' ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                    >
                      <CloseIcon />
                    </button>
                  </div>
                  <div className="space-y-2 mb-6">
                    <button 
                      onClick={() => toggleAnnotationTool('highlight')}
                      className={`flex items-center justify-center px-3 py-2 rounded w-full border ${
                        activeAnnotationTool === 'highlight' 
                          ? 'bg-blue-50 border-blue-300 text-blue-600' 
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <ButtonHighlightIcon />
                      Evidențiere
                    </button>
                    
                    <button 
                      onClick={() => toggleAnnotationTool('note')}
                      className={`flex items-center justify-center px-3 py-2 rounded w-full border ${
                        activeAnnotationTool === 'note' 
                          ? 'bg-blue-50 border-blue-300 text-blue-600' 
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <ButtonNoteIcon />
                      Notă
                    </button>
                    
                    <button 
                      onClick={() => toggleAnnotationTool('bookmark')}
                      className={`flex items-center justify-center px-3 py-2 rounded w-full border ${
                        activeAnnotationTool === 'bookmark' 
                          ? 'bg-blue-50 border-blue-300 text-blue-600' 
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <ButtonBookmarkIcon />
                      Semn de carte
                    </button>
                  </div>
                  
                  {activeAnnotationTool === 'highlight' && (
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Culoare evidențiere</h4>
                      <div className="flex space-x-2">
                        {['#FFEB3B', '#4CAF50', '#2196F3', '#E91E63', '#9C27B0'].map(color => (
                          <button
                            key={color}
                            onClick={() => setHighlightColor(color)}
                            className={`w-6 h-6 rounded-full border ${highlightColor === color ? 'ring-2 ring-blue-500' : 'ring-1 ring-gray-300'}`}
                            style={{ backgroundColor: color }}
                            title="Culoare evidențiere"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Adnotările tale</h4>
                    
                    {annotations.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">Nicio adnotare încă. Selectează un text și folosește uneltele de mai sus pentru a adăuga adnotări.</p>
                    ) : (
                      <div className="space-y-3">
                        {/* First list all bookmarks */}
                        {annotations
                          .filter(ann => ann.type === 'bookmark')
                          .map(annotation => (
                            <div 
                              key={idToString(annotation.id)} 
                              className="p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                              onClick={() => goToAnnotation(annotation)}
                            >
                              <div className="flex items-start justify-between mb-1">
                                <span className="flex items-center text-xs text-gray-500">
                                  <SmallBookmarkIcon />
                                  Pagina {annotation.position.page}
                                </span>
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeAnnotation(annotation.id);
                                  }}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  <SmallCloseIcon />
                                </button>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {typeof annotation.createdAt === 'string' 
                                  ? new Date(annotation.createdAt).toLocaleString('ro-RO')
                                  : annotation.createdAt.toLocaleString('ro-RO')}
                              </div>
                            </div>
                          ))}

                        {/* Then list all notes */}
                        {annotations
                          .filter(ann => ann.type === 'note')
                          .map(annotation => (
                            <div 
                              key={idToString(annotation.id)} 
                              className="p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                              onClick={() => goToAnnotation(annotation)}
                            >
                              <div className="flex items-start justify-between mb-1">
                                <span className="flex items-center text-xs text-gray-500">
                                  <SmallNoteIcon />
                                  Pagina {annotation.position.page}
                                </span>
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeAnnotation(annotation.id);
                                  }}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  <SmallCloseIcon />
                                </button>
                      </div>
                              <p className="text-sm text-gray-700 mb-1">{annotation.content}</p>
                              <div className="text-xs text-gray-500 mt-1">
                                {typeof annotation.createdAt === 'string' 
                                  ? new Date(annotation.createdAt).toLocaleString('ro-RO')
                                  : annotation.createdAt.toLocaleString('ro-RO')}
                  </div>
                </div>
                          ))}

                        {/* Finally list all highlights */}
                        {annotations
                          .filter(ann => ann.type === 'highlight')
                          .map(annotation => (
                            <div 
                              key={idToString(annotation.id)} 
                              className="p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                              onClick={() => goToAnnotation(annotation)}
                            >
                              <div className="flex items-start justify-between mb-1">
                                <span className="flex items-center text-xs text-gray-500">
                                  <SmallHighlightIcon color={annotation.color} />
                                  Pagina {annotation.position.page}
                                </span>
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeAnnotation(annotation.id);
                                  }}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  <SmallCloseIcon />
                                </button>
                              </div>
                              <p className="text-sm text-gray-700 mb-1">{annotation.content}</p>
                              <div className="text-xs text-gray-500 mt-1">
                                {typeof annotation.createdAt === 'string' 
                                  ? new Date(annotation.createdAt).toLocaleString('ro-RO')
                                  : annotation.createdAt.toLocaleString('ro-RO')}
                              </div>
                            </div>
                          ))}
                    </div>
                  )}
                </div>
                </div>
              </div>
            )}
              </div>
            </div>

        {/* Reading controls */}
        <div className={`fixed bottom-0 left-0 right-0 flex justify-between items-center p-4 ${
          readingMode === 'night' ? 'bg-gray-900 text-white' : 'bg-white text-gray-800'
        } shadow-lg z-10`}>
          <div className="flex items-center space-x-6">
            <button
              onClick={() => setFontSize(Math.max(12, fontSize - 1))}
              className="p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
              aria-label="Micșorează textul"
            >
              <MinusIcon />
            </button>
              <div className="flex items-center">
              <span className="text-sm mx-1">{fontSize}px</span>
            </div>
                <button
              onClick={() => setFontSize(Math.min(24, fontSize + 1))}
              className="p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
              aria-label="Mărește textul"
            >
              <PlusIcon />
                </button>
              </div>
              
          <div className="flex items-center space-x-2">
            <button
              onClick={goToPrevPage}
              disabled={currentPage === 1}
              className={`p-1.5 rounded-full ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'hover:bg-gray-200 dark:hover:bg-gray-700'}`}
              aria-label="Pagina anterioară"
            >
              <ArrowLeftIcon />
            </button>
            <div className="flex items-center space-x-2">
              <span className="text-sm">
                Pagina <span className="font-semibold">{currentPage}</span> din <span className="font-semibold">{totalPages}</span>
              </span>
                <input
                type="range"
                  min="1"
                max={totalPages}
                  value={currentPage}
                onChange={(e) => goToPage(parseInt(e.target.value))}
                className="w-24 h-1"
              />
              </div>
                <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className={`p-1.5 rounded-full ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'hover:bg-gray-200 dark:hover:bg-gray-700'}`}
              aria-label="Pagina următoare"
            >
              <ArrowRightIcon />
                </button>
          </div>
                
          <div className="flex items-center space-x-6">
                <button
              onClick={toggleReadingMode}
              className="p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
              aria-label={`Mod ${readingMode === 'night' ? 'zi' : 'noapte'}`}
                >
              {readingMode === 'night' ? <SunIcon /> : <MoonIcon />}
                </button>
                <button
              onClick={handleSaveToLibrary}
              className="p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
              aria-label="Salvează în biblioteca personală"
            >
              <BookmarkIcon />
                </button>
              </div>
            </div>
      </div>
    </div>
  );
};

export default BookReaderPreview; 
